import { useEffect, useRef, useState, useCallback } from 'react';
import { getWsUrl, authFetch, tryRestoreSession } from '../services/api';

const IS_DEV = process.env.NODE_ENV === 'development';
const DONE_STATUSES = ['done', 'completed', 'failed', 'error'];
const MAX_LOGS = 1200;
const JOB_POLL_MS = 2500;

const PHASE_MAP = {
  planning: 'Planning',
  architecting: 'Architecting',
  executing: 'Executing',
  testing: 'Testing',
  validating: 'Validating',
  qa_review: 'QA',
  qa: 'QA',
  deploying: 'Deploying',
  done: 'Deploying',
  completed: 'Deploying',
  failed: 'Deploying',
  error: 'Deploying',
};

const STOP_EVENTS = new Set([
  'orchestrator_completed',
  'pipeline_failed',
  'job_done',
  'phase2_completed',
  'job_completed',
  'job_finished',
]);

const AGENT_MAP = {
  pm: 'PM',
  architect: 'ARCHITECT',
  developer: 'DEVELOPER',
  developer_frontend: 'DEVELOPER',
  developer_backend: 'DEVELOPER',
  validator: 'VALIDATOR',
  qa: 'QA',
  debugger: 'DEBUGGER',
  devops: 'DEVOPS',
  test: 'TEST',
  sandbox: 'SANDBOX',
  orchestrator: 'DEVELOPER',
  contract: 'DEVELOPER',
  deploy: 'DEPLOY',
  install: 'INSTALL',
  build: 'BUILD',
  runtime: 'RUNTIME',
};

function wsDebug(...args) {
  if (IS_DEV) console.log('[useWebSocket]', ...args);
}

// AN ABNORMAL CLOSE IS DIAGNOSTIC, NOT NOISE. This was behind IS_DEV
// like wsDebug, so on a production build the close code never reached
// the console — and the close code is the single thing that separates
// "the server refused this" (4001/4003) from "something in the network
// path cut the connection" (1006 and friends). A user watching a live
// log say "RECONNECTING • retrying in 13s" on production had no way to
// tell which, and neither did anyone helping them. It only fires on a
// close that is already going to be reported in the UI.
function wsError(...args) {
  console.error('[useWebSocket]', ...args);
}

function normalizeJobId(jobId) {
  if (jobId == null || jobId === '') return '';
  return String(jobId).trim();
}

function normalizePhase(raw) {
  if (!raw) return null;
  const key = raw.toString().toLowerCase();
  return PHASE_MAP[key] || null;
}

function normalizeAgent(raw) {
  if (!raw) return null;
  const s = raw.toString().toLowerCase().trim();
  if (!s) return null;

  if (AGENT_MAP[s]) return AGENT_MAP[s];

  const base = s.split(/[_\s-]+/)[0];
  if (AGENT_MAP[base]) return AGENT_MAP[base];

  if (s.includes('developer')) return 'DEVELOPER';
  if (s.includes('architect')) return 'ARCHITECT';
  if (s.includes('validator')) return 'VALIDATOR';
  if (s.includes('debugger')) return 'DEBUGGER';
  if (s.includes('devops')) return 'DEVOPS';
  if (s.includes('sandbox')) return 'SANDBOX';
  if (s.includes('orchestrator')) return 'DEVELOPER';
  if (/\bqa\b/.test(s) || s.includes('qa_')) return 'QA';
  if (/\btest\b/.test(s) || s.startsWith('test_')) return 'TEST';
  if (/\bpm\b/.test(s) || s === 'pm' || s.startsWith('pm_')) return 'PM';

  return 'SANDBOX';
}

/**
 * Turn a raw Redis/WebSocket payload into a UI log row.
 */
export function parseWsPayload(data, { phaseRef, agentRef }) {
  if (!data || typeof data !== 'object') return null;

  const event = data.event || data.type || 'message';

  // Handle deployment/install/build/runtime log events
  const DEPLOY_EVENTS = new Set(['deployment_log', 'install_log', 'build_log', 'runtime_error', 'deployment_success', 'deployment_failed']);
  if (DEPLOY_EVENTS.has(event)) {
    const agentTypeMap = {
      deployment_log: 'DEPLOY',
      install_log: 'INSTALL',
      build_log: 'BUILD',
      runtime_error: 'RUNTIME',
      deployment_success: 'DEPLOY',
      deployment_failed: 'DEPLOY',
    };
    const typeMap = {
      deployment_success: 'success',
      deployment_failed: 'error',
      runtime_error: 'error',
    };
    return {
      event,
      detail: (data.message || data.detail || data.error || event).trim(),
      type: typeMap[event] || '',
      phase: 'Deploying',
      agentType: agentTypeMap[event] || 'DEPLOY',
    };
  }

  let nextPhase = null;
  const statusRaw = data.to_status || data.phase || data.stage || data.status;
  if (statusRaw) nextPhase = normalizePhase(statusRaw);

  if (data.event === 'state_transition' && nextPhase) {
    phaseRef.current = nextPhase;
  } else if (event === 'phase2_started') {
    phaseRef.current = 'Executing';
    nextPhase = 'Executing';
  }

  const phase = nextPhase || phaseRef.current || 'Executing';

  const agentRaw = data.agent || data.agent_type || data.from_agent || data.current_agent;
  const nextAgent = normalizeAgent(agentRaw);
  if (nextAgent) agentRef.current = nextAgent;
  const agentType = nextAgent || agentRef.current || 'SANDBOX';

  let detail = '';
  let fileProgress = null;
  if (data.type === 'file_progress' || event === 'file_progress') {
    const file = data.file || data.path || data.filename || '';
    const status = data.status || 'update';
    detail = data.message || (file ? `${status}: ${file}` : String(status));
    if (data.percent != null) detail += ` (${data.percent}%)`;
    // Keep the structured fields too — GeneratingProgress reads these
    // directly instead of re-parsing the flattened `detail` string (it
    // isn't JSON, so JSON.parse(detail) was silently failing every time,
    // which is why the progress bar always stuck at 0%).
    fileProgress = { file, status, percent: data.percent != null ? data.percent : null };
  } else if (data.type === 'file_started' || event === 'file_started') {
    detail = data.path || data.file || '';
  } else if (data.type === 'file_completed' || event === 'file_completed') {
    detail = data.path || data.file || '';
  } else if (data.type === 'status' || event === 'status') {
    detail = data.message || data.detail || '';
  } else if (data.type === 'file_created' || event === 'file_created') {
    detail = data.message || `Created ${data.file || 'file'}`;
  } else {
    const parts = [];
    if (data.from_status && data.to_status) parts.push(`${data.from_status} → ${data.to_status}`);
    if (data.tokens != null) parts.push(`tokens=${data.tokens}`);
    const text = data.message || data.content || data.detail || data.msg;
    if (text) parts.push(String(text));
    if (data.error) parts.push(String(data.error));
    if (data.file) parts.push(String(data.file));
    detail = parts.join(' ');
  }

  if (!detail) detail = String(event);

  // SECURITY-SCAN-PREVIEW-WARNING-FIX: same reason fileProgress is kept
  // structured below instead of only flattened into `detail` — the
  // security banner in ProjectWorkspace.js reads `blocked`/`summary`
  // directly, and detail is free text, not JSON, so it can't be
  // reconstructed from the flattened string.
  const securityScan =
    event === 'security_scan_completed'
      ? { blocked: !!data.blocked, summary: data.summary || {} }
      : null;

  const eventLower = String(event).toLowerCase();
  const type =
    eventLower.includes('error') || eventLower.includes('fail')
      ? 'error'
      : eventLower.includes('done') || eventLower.includes('completed') || eventLower === 'file_progress'
        ? 'success'
        : data.type === 'file_progress'
          ? 'success'
          : '';

  return {
    event: String(event),
    detail: detail.trim(),
    path: data.path || data.file || undefined,
    type,
    phase,
    agentType,
    ...(fileProgress ? { fileProgress } : {}),
    ...(securityScan ? { blocked: securityScan.blocked, summary: securityScan.summary } : {}),
  };
}

// Roughly fifteen minutes of backoff (1,2,4,8,16,30,30,...). Past that a
// socket nobody can see is reconnecting against a backend that is not
// coming back on its own.
const MAX_RECONNECT_ATTEMPTS = 30;

export default function useWebSocket(jobId, options = {}) {
  const { enabled = true, jobStatus = null } = options;
  const normalizedJobId = normalizeJobId(jobId);

  const [logs, setLogs] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const [reconnectCountdown, setReconnectCountdown] = useState(0);
  // Why the socket stopped trying, when it has stopped for good. null
  // while it is connected or still retrying.
  const [fatalError, setFatalError] = useState(null);
  // Refs, not state: onclose reads these synchronously and must not wait
  // for a re-render to know whether it has already tried.
  const authRetriedRef = useRef(false);
  const reconnectAttemptRef = useRef(0);

  const wsRef = useRef(null);
  const shouldConnectRef = useRef(false);
  const isMountedRef = useRef(true);
  const activeJobIdRef = useRef('');

  const reconnectTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const connectRef = useRef(null);

  const phaseRef = useRef('Planning');
  const agentRef = useRef('DEVELOPER');
  const logIdRef = useRef(0);

  const lastPolledStatusRef = useRef(null);
  const lastPolledAgentRef = useRef(null);

  const addLog = useCallback((entry) => {
    setLogs((prev) => {
      const row = {
        id: ++logIdRef.current,
        time: new Date().toLocaleTimeString(),
        ...entry,
      };
      const next = prev.concat(row);
      if (next.length <= MAX_LOGS) return next;
      return next.slice(next.length - MAX_LOGS);
    });
  }, []);

  const canConnect = Boolean(enabled && normalizedJobId);

  useEffect(() => {
    shouldConnectRef.current = canConnect;
  }, [canConnect]);

  const clearReconnectTimers = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const closeSocket = useCallback(() => {
    const ws = wsRef.current;
    if (!ws) return;
    try {
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      ws.close(1000);
    } catch (err) {
      wsError('closeSocket', err);
    }
    wsRef.current = null;
  }, []);

  const connect = useCallback(() => {
    if (!shouldConnectRef.current || !normalizedJobId) return;

    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    clearReconnectTimers();

    const url = getWsUrl(normalizedJobId);
    if (!url) {
      wsError('Missing WebSocket URL — jobId:', normalizedJobId);
      return;
    }

    wsDebug('connecting', url);

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!isMountedRef.current) return;
        wsDebug('WS CONNECTED', url);
        setIsConnected(true);
        // A connection that opened proves the token and the transport are
        // both fine again — both latches reset.
        authRetriedRef.current = false;
        reconnectAttemptRef.current = 0;
        setFatalError(null);
        setReconnectAttempt(0);
        setReconnectCountdown(0);
      };

      ws.onmessage = (e) => {
        if (!isMountedRef.current) return;

        let data;
        try {
          const raw = typeof e.data === 'string' ? e.data : String(e.data);
          if (IS_DEV) wsDebug('RAW WS MESSAGE', raw.slice(0, 500));
          data = JSON.parse(raw);
        } catch (err) {
          wsError('WS PARSE ERROR', err, e.data);
          addLog({
            event: 'websocket_parse_error',
            detail: String(e.data || '').slice(0, 240),
            type: 'error',
            phase: phaseRef.current || 'Executing',
            agentType: 'SANDBOX',
          });
          return;
        }

        if (IS_DEV) wsDebug('PARSED LOG', data);

        const payloadJobId = normalizeJobId(data.job_id || data.jobId);
        if (payloadJobId && payloadJobId !== normalizedJobId) {
          return;
        }

        const event = data.event || data.type || 'message';
        if (STOP_EVENTS.has(event)) {
          shouldConnectRef.current = false;
          clearReconnectTimers();
          setReconnectAttempt(0);
          setReconnectCountdown(0);
        }

        const entry = parseWsPayload(data, { phaseRef, agentRef });
        if (entry) addLog(entry);
      };

      ws.onerror = (err) => {
        wsError('WS ERROR', err);
        if (!isMountedRef.current) return;
        setIsConnected(false);
      };

      ws.onclose = (ev) => {
        wsDebug('WS CLOSED', { code: ev?.code, reason: ev?.reason });
        if (!isMountedRef.current) return;

        if (wsRef.current !== ws) return;
        wsRef.current = null;
        setIsConnected(false);

        const normalClose = ev && ev.code === 1000;
        if (!shouldConnectRef.current || normalClose) return;

        // SOME CLOSES CANNOT BE RETRIED AWAY.
        // backend/api/websocket.py closes with 4001 when the token is
        // missing, expired, or not an access token, and 4003 when the job
        // does not belong to this user. Every non-1000 close was treated
        // as transient, so the socket reconnected on a backoff capped at
        // 30s — forever, with the SAME expired token, getting the SAME
        // 4001 each time. The job ran fine; the Live Log just never came
        // back, and nothing said why.
        //
        // 4001 is worth exactly one recovery attempt: the refresh cookie
        // can mint a new access token, and getWsUrl reads it fresh on the
        // next connect. If that still fails, the session is genuinely
        // gone and retrying is noise.
        const code = ev?.code;
        if (code === 4003) {
          wsError('WS refused: this job belongs to another account');
          setFatalError('This log belongs to a different account.');
          shouldConnectRef.current = false;
          return;
        }
        if (code === 4001) {
          if (authRetriedRef.current) {
            wsError('WS refused twice after a token refresh — giving up');
            setFatalError('Your session expired. Reload the page to see live logs.');
            shouldConnectRef.current = false;
            return;
          }
          authRetriedRef.current = true;
          wsDebug('WS refused (4001) — refreshing the access token once');
          tryRestoreSession().then((ok) => {
            if (!isMountedRef.current || !shouldConnectRef.current) return;
            if (!ok) {
              setFatalError('Your session expired. Reload the page to see live logs.');
              shouldConnectRef.current = false;
              return;
            }
            connectRef.current?.();
          });
          return;
        }

        // Say WHICH close this was, on every build. 1006 means the
        // connection was cut without a close frame — a proxy timeout, an
        // idle timeout or a dropped upgrade — and is the code that tells
        // an operator to look at nginx rather than at the application.
        wsError('WS closed', code, ev?.reason ? `(${ev.reason})` : '', '— will retry');

        // A transport-level drop IS worth retrying, but not without end:
        // a backend that is down stays down, and an invisible socket
        // reconnecting every 30s for an hour helps nobody.
        if (reconnectAttemptRef.current >= MAX_RECONNECT_ATTEMPTS) {
          wsError('WS gave up after', MAX_RECONNECT_ATTEMPTS, 'attempts');
          setFatalError('Lost the connection to the server. Reload the page to reconnect.');
          shouldConnectRef.current = false;
          return;
        }

        reconnectAttemptRef.current += 1;
        setReconnectAttempt((prev) => {
          const nextAttempt = prev + 1;
          const delaySec = Math.min(2 ** (nextAttempt - 1), 30);

          clearReconnectTimers();
          setReconnectCountdown(delaySec);

          countdownIntervalRef.current = setInterval(() => {
            setReconnectCountdown((c) => (c <= 1 ? 0 : c - 1));
          }, 1000);

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            connectRef.current?.();
          }, delaySec * 1000);

          return nextAttempt;
        });
      };
    } catch (err) {
      wsError('connect failed', err);
    }
  }, [addLog, clearReconnectTimers, normalizedJobId]);

  connectRef.current = connect;

  // WebSocket lifecycle — do NOT set shouldConnectRef=false in cleanup (fixes reconnect / StrictMode)
  useEffect(() => {
    isMountedRef.current = true;

    if (!canConnect) {
      setIsConnected(false);
      setReconnectAttempt(0);
      setReconnectCountdown(0);
      clearReconnectTimers();
      closeSocket();
      return undefined;
    }

    const jobChanged = activeJobIdRef.current !== normalizedJobId;
    if (jobChanged) {
      activeJobIdRef.current = normalizedJobId;
      logIdRef.current = 0;
      setLogs([]);
      phaseRef.current = 'Planning';
      agentRef.current = 'DEVELOPER';
      lastPolledStatusRef.current = null;
      lastPolledAgentRef.current = null;
      wsDebug('job changed, reset logs', normalizedJobId);
    }

    connect();

    return () => {
      isMountedRef.current = false;
      clearReconnectTimers();
      closeSocket();
    };
  }, [canConnect, normalizedJobId, connect, clearReconnectTimers, closeSocket]);

  // REST fallback: synthesize logs from /api/jobs/{id} when WS/redis stream is quiet
  useEffect(() => {
    if (!normalizedJobId || !enabled) return undefined;

    let cancelled = false;

    const pollJob = async () => {
      try {
        const res = await authFetch(`/api/jobs/${normalizedJobId}`);
        if (!res.ok || cancelled) return;
        const job = await res.json();
        if (cancelled) return;

        const status = job.status || job.state;
        const agent = job.current_agent || job.currentAgent;

        if (status && status !== lastPolledStatusRef.current) {
          const prev = lastPolledStatusRef.current;
          lastPolledStatusRef.current = status;
          const phase = normalizePhase(status) || phaseRef.current || 'Executing';
          phaseRef.current = phase;

          addLog({
            event: prev ? 'job_status' : 'job_started',
            detail: prev ? `${prev} → ${status}` : `Job status: ${status}`,
            type: DONE_STATUSES.includes(status) ? (status === 'failed' || status === 'error' ? 'error' : 'success') : '',
            phase,
            agentType: agentRef.current,
          });
        }

        if (agent && agent !== lastPolledAgentRef.current) {
          lastPolledAgentRef.current = agent;
          const agentType = normalizeAgent(agent) || agentRef.current;
          agentRef.current = agentType;
          addLog({
            event: 'agent_active',
            detail: `Agent: ${agent}`,
            type: '',
            phase: phaseRef.current || 'Executing',
            agentType,
          });
        }
      } catch (err) {
        if (IS_DEV) wsDebug('job poll failed', err?.message);
      }
    };

    pollJob();
    const interval = setInterval(pollJob, JOB_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [normalizedJobId, enabled, addLog]);

  return {
    logs, isConnected, connected: isConnected,
    reconnectAttempt, reconnectCountdown,
    // "" / null while healthy; a sentence to show the user once the
    // socket has given up for a reason retrying cannot fix.
    fatalError,
  };
}
